import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Logo from '../common/Logo';
import './Navbar.css';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [hoverTimeout, setHoverTimeout] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMobile = () => setMobileOpen(!mobileOpen);
  const closeMobile = () => setMobileOpen(false);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    closeMobile();
    setOpenDropdown(null);
  };

  const navigateTo = (path) => {
    navigate(path);
    closeMobile();
    setOpenDropdown(null);
  };

  const handleMouseEnter = (menuId) => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
    setOpenDropdown(menuId);
  };

  const handleDropdownMouseEnter = () => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
  };

  const handleDropdownMouseLeave = () => {
    const timeout = setTimeout(() => {
      setOpenDropdown(null);
    }, 200);
    setHoverTimeout(timeout);
  };

  const menuItems = [
    {
      id: 'home',
      label: 'خانه',
      link: '/',
      hasDropdown: false,
    },
    {
      id: 'internet',
      label: 'اینترنت پرسرعت',
      link: '/internet',
      hasDropdown: true,
      items: [
        { label: 'TD-LTE هوشمند', link: '/internet/tdlte' },
        { label: 'P2P اختصاصی', link: '/internet/p2p' },
      ],
    },
    {
      id: 'domain',
      label: 'دامنه',
      link: '/domain',
      hasDropdown: true,
      items: [
        { label: 'ثبت دامنه ملی', link: '/domain/national' },
        { label: 'ثبت دامنه بین‌المللی', link: '/domain/international' },
        { label: 'انتقال دامنه', link: '/domain/transfer' },
      ],
    },
    {
      id: 'hosting',
      label: 'هاست',
      link: '/hosting',
      hasDropdown: true,
      items: [
        { label: 'هاست cPanel ایران', link: '/hosting/cpanel-iran' },
        { label: 'هاست cPanel خارج', link: '/hosting/cpanel-foreign' },
        { label: 'هاست لینوکس', link: '/hosting/linux' },
        { label: 'هاست ویندوز (به‌زودی)', link: '/hosting/windows' },
        { label: 'هاست DirectAdmin', link: '/hosting/directadmin' },
        { label: 'هاست وردپرس', link: '/hosting/wordpress' },
      ],
    },
    {
      id: 'vps',
      label: 'سرور مجازی',
      link: '/vps',
      hasDropdown: true,
      items: [
        { label: 'سرور مجازی لینوکس', link: '/vps/linux' },
        { label: 'سرور مجازی ویندوز', link: '/vps/windows' },
        { label: 'سرور مجازی مدیریت‌شده', link: '/vps/managed' },
        { label: 'سرور مجازی هلند', link: '/vps/netherlands' },
        { label: 'سرور مجازی آلمان', link: '/vps/germany' },
        { label: 'سرور مجازی میکروتیک', link: '/vps/mikrotik' },
        { label: 'سرور مجازی ترکیه', link: '/vps/turkey' },
        { label: 'سرور مجازی آمریکا', link: '/vps/usa' },
        { label: 'سرور مجازی انگلستان', link: '/vps/uk' },
        { label: 'سرور مجازی فرانسه', link: '/vps/france' },
        { label: 'سرور مجازی کانادا', link: '/vps/canada' },
      ],
    },
    {
      id: 'dedicated',
      label: 'سرور اختصاصی',
      link: '/dedicated',
      hasDropdown: true,
      items: [
        { label: 'سرور اختصاصی مدیریت‌شده', link: '/dedicated/managed' },
        { label: 'سرور اختصاصی ایران', link: '/dedicated/iran' },
        { label: 'سرور اختصاصی ترکیه', link: '/dedicated/turkey' },
        { label: 'سرور اختصاصی هلند', link: '/dedicated/netherlands' },
        { label: 'کلوکیشن', link: '/dedicated/colocation' },
      ],
    },
    {
      id: 'license',
      label: 'لایسنس',
      link: '/licenses',
      hasDropdown: true,
      items: [
        { label: 'لایسنس cPanel', link: '/licenses/cpanel' },
        { label: 'لایسنس DirectAdmin', link: '/licenses/directadmin' },
        { label: 'لایسنس ImmunifyAV', link: '/licenses/immunify' },
        { label: 'لایسنس LiteSpeed', link: '/licenses/litespeed' },
        { label: 'لایسنس CloudLinux', link: '/licenses/cloudlinux' },
        { label: 'لایسنس JetBackup', link: '/licenses/jetbackup' },
        { label: 'لایسنس Softaculous', link: '/licenses/softaculous' },
        { label: 'لایسنس Virtualizor', link: '/licenses/virtualizor' },
        { label: 'لایسنس Plesk', link: '/licenses/plesk' },
        { label: 'لایسنس KernelCare', link: '/licenses/kernelcare' },
        { label: 'لایسنس SolusVM', link: '/licenses/solusvm' },
        { label: 'لایسنس WHM Reseller', link: '/licenses/whmreseller' },
        { label: 'لایسنس DirectAdmin Reseller', link: '/licenses/directadminreseller' },
        { label: 'لایسنس CPNginx', link: '/licenses/cpnginx' },
        { label: 'لایسنس SitePad', link: '/licenses/sitepad' },
        { label: 'لایسنس OSM', link: '/licenses/osm' },
        { label: 'لایسنس WHMCS Sonic', link: '/licenses/whmcssonic' },
        { label: 'لایسنس MailScaner', link: '/licenses/mailscaner' },
      ],
    },
    {
      id: 'config',
      label: 'کانفیگ سرور',
      link: '/server-config',
      hasDropdown: true,
      items: [
        { label: 'کانفیگ سرور cPanel', link: '/server-config/cpanel' },
        { label: 'کانفیگ سرور DirectAdmin', link: '/server-config/directadmin' },
        { label: 'کانفیگ سرور Virtualizor', link: '/server-config/virtualizor' },
        { label: 'کانفیگ سرور ESXi', link: '/server-config/esxi' },
        { label: 'کانفیگ Docker', link: '/server-config/docker' },
        { label: 'کانفیگ سرور Plesk', link: '/server-config/plesk' },
      ],
    },
    {
      id: 'whmcs',
      label: 'خدمات WHMCS',
      link: '/whmcs',
      hasDropdown: true,
      items: [
        { label: 'خرید لایسنس WHMCS', link: '/whmcs/license' },
        { label: 'نصب و کانفیگ WHMCS', link: '/whmcs/install' },
        { label: 'ماژول WHMCS', link: '/whmcs/modules' },
        { label: 'ماژول درگاه پرداخت WHMCS', link: '/whmcs/payment-gateway' },
        { label: 'قالب WHMCS', link: '/whmcs/template' },
      ],
    },
  ];

  const handleMainLinkClick = (e, item) => {
    e.preventDefault();
    
    if (item.link && item.link.startsWith('/')) {
      navigateTo(item.link);
    } else if (item.link && item.link.startsWith('#')) {
      if (window.location.pathname !== '/') {
        navigateTo('/');
        setTimeout(() => scrollTo(item.link.replace('#', '')), 100);
      } else {
        scrollTo(item.link.replace('#', ''));
      }
    }
    closeMobile();
  };

  const handleSubItemClick = (subItem) => {
    if (subItem.link && subItem.link.startsWith('/')) {
      navigateTo(subItem.link);
    } else if (subItem.link && subItem.link.startsWith('#')) {
      if (window.location.pathname !== '/') {
        navigateTo('/');
        setTimeout(() => scrollTo(subItem.link.replace('#', '')), 100);
      } else {
        scrollTo(subItem.link.replace('#', ''));
      }
    }
    closeMobile();
    setOpenDropdown(null);
  };

  return (
    <>
      <nav className={`navbar ${scrolled ? 'stuck' : ''}`}>
        <div className="wrap nav-inner">
          <Logo />
          <ul className="nav-links">
            {menuItems.map((item) => (
              <li 
                key={item.id} 
                className={`nav-item ${item.hasDropdown ? 'has-dropdown' : ''} ${openDropdown === item.id ? 'dropdown-open' : ''}`}
                onMouseEnter={() => item.hasDropdown && handleMouseEnter(item.id)}
                onMouseLeave={() => item.hasDropdown && handleDropdownMouseLeave()}
              >
                <a 
                  href={item.link || '#'} 
                  className="nav-link"
                  onClick={(e) => handleMainLinkClick(e, item)}
                >
                  {item.label}
                  {item.hasDropdown && <span className="dropdown-arrow">▾</span>}
                </a>
                {item.hasDropdown && (
                  <ul 
                    className="dropdown-menu"
                    onMouseEnter={handleDropdownMouseEnter}
                    onMouseLeave={handleDropdownMouseLeave}
                  >
                    {item.items.map((subItem, index) => (
                      <li key={index}>
                        <button onClick={() => handleSubItemClick(subItem)}>
                          {subItem.label}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
          <div className="nav-end">
            <a href="https://hub.afracell.com" className="btn-outline" target="_blank" rel="noopener noreferrer">پنل مشتریان</a>
            <button className="btn-fill" onClick={() => {
              if (window.location.pathname !== '/') {
                navigateTo('/');
                setTimeout(() => scrollTo('contact'), 100);
              } else {
                scrollTo('contact');
              }
            }}>مشاوره رایگان</button>
          </div>
          <button className="hamburger" onClick={toggleMobile} aria-label="منو">
            <span></span><span></span><span></span>
          </button>
        </div>
      </nav>
      <div className={`mob-menu ${mobileOpen ? 'open' : ''}`}>
        {menuItems.map((item) => (
          <div key={item.id} className="mob-menu-item">
            {item.hasDropdown ? (
              <>
                <div className="mob-menu-label" onClick={() => {
                  if (openDropdown === item.id) {
                    setOpenDropdown(null);
                  } else {
                    setOpenDropdown(item.id);
                  }
                }}>
                  {item.label}
                  <span className="dropdown-arrow">{openDropdown === item.id ? '▴' : '▾'}</span>
                </div>
                <div className={`mob-submenu ${openDropdown === item.id ? 'open' : ''}`}>
                  {item.items.map((subItem, index) => (
                    <button key={index} onClick={() => handleSubItemClick(subItem)}>
                      {subItem.label}
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <Link to={item.link} onClick={closeMobile}>
                {item.label}
              </Link>
            )}
          </div>
        ))}
        <a href="https://hub.afracell.com" target="_blank" rel="noopener noreferrer" className="mob-panel-link">پنل مشتریان</a>
        <button className="btn-fill" onClick={() => {
          if (window.location.pathname !== '/') {
            navigateTo('/');
            setTimeout(() => scrollTo('contact'), 100);
          } else {
            scrollTo('contact');
          }
          closeMobile();
        }}>مشاوره رایگان</button>
      </div>
    </>
  );
};

export default Navbar;
